/**
 * TipTap StatusBadge inline atom node — Notion-style colored status badges.
 *
 * 100% frontend (no Drupal entity, no API call).
 * HTML: <span data-status-badge data-badge-color="blue">Label</span>
 * Label is stored as text content (not an attribute) for readability.
 *
 * @module status-badge-node
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Palette, Plus, Trash2 } from 'lucide-react';

const BADGE_COLORS = [
    { key: 'default', label: 'Gris pierre', classes: 'bg-stone-100 text-stone-700 border-stone-200' },
    { key: 'gray',    label: 'Gris',        classes: 'bg-stone-100 text-stone-700 border-stone-200' },
    { key: 'brown',   label: 'Brun',        classes: 'bg-amber-50 text-amber-800 border-amber-200' },
    { key: 'orange',  label: 'Orange',       classes: 'bg-orange-50 text-orange-800 border-orange-200' },
    { key: 'yellow',  label: 'Jaune',        classes: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
    { key: 'green',   label: 'Vert',         classes: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
    { key: 'blue',    label: 'Bleu',         classes: 'bg-blue-50 text-blue-800 border-blue-200' },
    { key: 'purple',  label: 'Violet',       classes: 'bg-purple-50 text-purple-800 border-purple-200' },
    { key: 'pink',    label: 'Rose',         classes: 'bg-pink-50 text-pink-800 border-pink-200' },
    { key: 'red',     label: 'Rouge',        classes: 'bg-red-50 text-red-800 border-red-200' },
];

const BADGE_COLOR_MAP = Object.fromEntries(BADGE_COLORS.map((c) => [c.key, c.classes]));

const StatusBadgeNode = Node.create({
    name: 'statusBadge',
    inline: true,
    group: 'inline',
    atom: true,
    selectable: true,

    addAttributes() {
        return {
            label: {
                default: '',
                parseHTML: (el) => el.textContent || '',
                renderHTML: () => ({}),
            },
            color: {
                default: 'default',
                parseHTML: (el) => el.getAttribute('data-badge-color') || 'default',
                renderHTML: (attrs) => ({ 'data-badge-color': attrs.color }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-status-badge]' }];
    },

    renderHTML({ node }) {
        return ['span', {
            'data-status-badge': '',
            'data-badge-color': node.attrs.color,
        }, node.attrs.label];
    },

    addNodeView() {
        return ReactNodeViewRenderer(StatusBadgeView);
    },
});

export default StatusBadgeNode;

const PRESET_BADGES = [
    { label: 'En cours',    color: 'blue' },
    { label: 'Terminé',     color: 'green' },
    { label: 'En attente',  color: 'yellow' },
    { label: 'Urgent',      color: 'red' },
    { label: 'En revue',    color: 'purple' },
    { label: 'Annulé',      color: 'gray' },
    { label: 'Brouillon',   color: 'default' },
    { label: 'Bloqué',      color: 'orange' },
    { label: 'Validé',      color: 'green' },
    { label: 'À planifier', color: 'brown' },
];

function StatusBadgeView({ node, updateAttributes, editor, deleteNode }) {
    const [popupOpen, setPopupOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [mode, setMode] = useState('list');
    const [editLabel, setEditLabel] = useState('');
    const [editColor, setEditColor] = useState('blue');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const labelInputRef = useRef(null);
    const isEditable = editor?.isEditable;

    const colorClasses = BADGE_COLOR_MAP[node.attrs.color] || BADGE_COLOR_MAP.default;

    const filteredBadges = useMemo(() => {
        if (!search.trim()) return PRESET_BADGES;
        const q = search.toLowerCase();
        return PRESET_BADGES.filter((b) => b.label.toLowerCase().includes(q));
    }, [search]);

    const handleClick = useCallback(() => {
        if (!isEditable) return;
        setPopupOpen(true);
        setMode('list');
        setSearch('');
    }, [isEditable]);

    useEffect(() => {
        if (!popupOpen) return;
        const focusTimer = setTimeout(() => {
            if (mode === 'list') searchInputRef.current?.focus();
            else labelInputRef.current?.focus();
        }, 50);
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setPopupOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => { clearTimeout(focusTimer); document.removeEventListener('mousedown', handler); };
    }, [popupOpen, mode]);

    const selectBadge = useCallback((badge) => {
        updateAttributes({ label: badge.label, color: badge.color });
        setPopupOpen(false);
    }, [updateAttributes]);

    const openEditMode = useCallback(() => {
        setEditLabel(node.attrs.label || '');
        setEditColor(node.attrs.color || 'blue');
        setMode('edit');
    }, [node.attrs.label, node.attrs.color]);

    const openCreateMode = useCallback(() => {
        setEditLabel('');
        setEditColor('blue');
        setMode('create');
    }, []);

    const applyEdit = useCallback(() => {
        const trimmed = editLabel.trim();
        if (!trimmed) return;
        updateAttributes({ label: trimmed, color: editColor });
        setPopupOpen(false);
        setMode('list');
    }, [editLabel, editColor, updateAttributes]);

    const badgeForm = (
        <div className="p-3 space-y-3">
            <p className="text-xs font-medium text-stone-500">
                {mode === 'edit' ? 'Modifier le badge' : 'Nouveau badge'}
            </p>
            <input
                ref={labelInputRef}
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                placeholder="Label du badge"
                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-stone-200 focus:outline-none focus:border-stone-400"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); applyEdit(); }
                    if (e.key === 'Escape') setMode('list');
                }}
            />
            <div className="flex flex-wrap gap-1">
                {BADGE_COLORS.filter((c) => c.key !== 'default').map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => setEditColor(c.key)}
                        title={c.label}
                        className={`w-6 h-6 rounded-full border-2 transition-all cursor-pointer ${c.classes} ${editColor === c.key ? 'ring-2 ring-offset-1 ring-blue-500 scale-110' : ''}`}
                    />
                ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${BADGE_COLOR_MAP[editColor]}`}>
                    {editLabel || 'Aperçu'}
                </span>
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setMode('list')}
                    className="flex-1 px-3 py-1.5 text-xs text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg cursor-pointer"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={applyEdit}
                    disabled={!editLabel.trim()}
                    className="flex-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg cursor-pointer"
                >
                    {mode === 'edit' ? 'Appliquer' : 'Créer'}
                </button>
            </div>
        </div>
    );

    return (
        <NodeViewWrapper as="span" className="inline relative" ref={containerRef}>
            <span
                onClick={handleClick}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClasses} ${isEditable ? 'cursor-pointer hover:opacity-80' : ''}`}
            >
                {node.attrs.label || 'Statut'}
            </span>
            {popupOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden text-sm">
                    {mode === 'list' ? (
                        <>
                            <div className="px-2 pt-2 pb-1">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Rechercher un badge…"
                                    className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-stone-200 focus:outline-none focus:border-stone-400 bg-stone-50"
                                    onKeyDown={(e) => e.key === 'Escape' && setPopupOpen(false)}
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto px-1 py-1">
                                {filteredBadges.length === 0 && (
                                    <p className="text-stone-400 text-center py-3 text-xs">Aucun badge trouvé</p>
                                )}
                                {filteredBadges.map((badge, i) => {
                                    const bc = BADGE_COLOR_MAP[badge.color] || BADGE_COLOR_MAP.default;
                                    return (
                                        <button
                                            key={`preset-${i}`}
                                            type="button"
                                            onClick={() => selectBadge(badge)}
                                            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-stone-50 text-left cursor-pointer"
                                        >
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${bc}`}>
                                                {badge.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="border-t border-stone-100 px-2 py-1.5">
                                {node.attrs.label && node.attrs.label !== 'Statut' && (
                                    <button
                                        type="button"
                                        onClick={openEditMode}
                                        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs text-stone-600 hover:bg-stone-50 rounded-lg cursor-pointer"
                                    >
                                        <Palette size={13} /> Modifier ce badge
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={openCreateMode}
                                    className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                                >
                                    <Plus size={13} /> Créer un badge personnalisé
                                </button>
                                {node.attrs.label && (
                                    <button
                                        type="button"
                                        onClick={() => { deleteNode(); setPopupOpen(false); }}
                                        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                                    >
                                        <Trash2 size={13} /> Retirer le badge
                                    </button>
                                )}
                            </div>
                        </>
                    ) : badgeForm}
                </div>
            )}
        </NodeViewWrapper>
    );
}
